/**
 * CountsTab — Lista de conteos con acciones: ver detalle, completar, anular, escanear.
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, CheckCircle, Clock, Camera, Eye, XCircle, CheckSquare, ChevronDown, ChevronRight, ClipboardList } from 'lucide-react'
import { useStockCounts, useCreateStockCount, useCompleteStockCount, useCancelStockCount, useStockCount, useUpdateStockCount, type StockCount } from '../hooks/useInventory'
import { usePermission } from '../../../hooks/usePermission'
import { useTenantLocale, formatNumber } from '../../../lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { ConfirmModal, useConfirmModal } from '../../../components/ConfirmModal'
import { extractErrorMessage } from '../../../lib/errorUtils'
import CountModal from './CountModal'
import { Pagination } from '@/components/ds/Pagination'
import { DataTable } from '@/components/ds/DataTable'

/** Una línea del conteo, con su posición y lo que dos columnas necesitan. */
interface FilaDeConteo {
  idx: number
  diff: number
  esNuevo: boolean
  product_name?: string
  product_sku?: string
  expected_qty?: string
  counted_qty?: string
}
import { EmptyState } from '@/components/EmptyState'

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  draft:       { label: 'Borrador', cls: 'bg-surface-muted text-content-muted' },
  in_progress: { label: 'En Progreso', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  completed:   { label: 'Completado', cls: 'bg-success-surface-strong text-success-content' },
  cancelled:   { label: 'Anulado', cls: 'bg-danger-surface-strong text-danger-content' },
}

export default function CountsTab() {
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR: esta lista la pagina el backend (`?ordering=`).
  const [orden, setOrden] = useState<SortState | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [expandedId, setExpandedId] = useState('')
  const { can } = usePermission()
  const navigate = useNavigate()
  const locale = useTenantLocale()
  const toast = useToast()

  const { data, isLoading } = useStockCounts({ page, page_size: 20, ordering: sortStateToParam(orden) })
  const { mutate: createCount, isPending } = useCreateStockCount()

  const counts = data?.counts || []
  const total = data?.count || 0

  const fmtQty = (v: string | number) => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)
  }

  const handleCreate = (formData: any) => {
    createCount(formData, {
      onSuccess: () => { toast.success('Conteo creado. Los productos se cargaron automáticamente.'); setModalOpen(false) },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al crear conteo')),
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{total} conteos</span>
        {can('inventory.counts', 'create') && (
          <button onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nuevo conteo
          </button>
        )}
      </div>

      <DataTable<StockCount>
        {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          { key: 'expandir', header: '', width: 32,
            render: c => (
              <span className="text-content-subtle">
                {expandedId === c.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            ) },
          { key: 'warehouse_code', header: 'Bodega', sortable: true,
            render: c => (
              <span className="font-medium text-content">
                {c.warehouse_code} — {c.warehouse_name}
              </span>
            ) },
          { key: 'count_date', header: 'Fecha', sortable: true,
            render: c => <span className="text-content-muted">{c.count_date}</span> },
          { key: 'status', header: 'Estado',
            sortable: true,
            sortValue: c => (STATUS_BADGE[c.status] || STATUS_BADGE.draft).label,
            render: c => {
              const badge = STATUS_BADGE[c.status] || STATUS_BADGE.draft
              return (
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${badge.cls}`}>
                  {badge.label}
                </span>
              )
            } },
          { key: 'lines_count', header: 'Productos', numeric: true, sortValue: c => c.lines_count || 0,
            render: c => c.lines_count || 0 },
          { key: 'differences_count', header: 'Diferencias', numeric: true, sortValue: c => c.differences_count || 0,
            render: c => ((c.differences_count || 0) > 0
              ? <span className="text-warning-content-soft font-medium">{c.differences_count}</span>
              : <span className="text-content-subtle">0</span>) },
          { key: 'accounting_status', header: 'Contab.',
            render: c => (c.accounting_status === 'accounted'
              ? <CheckCircle size={16} className="text-success-solid" />
              : <Clock size={16} className="text-content-subtle" />) },
          { key: 'acciones', header: 'Acciones', width: 112,
            // `stopPropagation` para que las acciones no expandan la fila de
            // paso: el clic de la fila es lo que abre el detalle.
            render: c => (
              <div onClick={e => e.stopPropagation()}>
                <CountActions count={c} navigate={navigate} toast={toast} can={can} />
              </div>
            ) },
        ]}
        rows={counts}
        rowKey={c => c.id}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ClipboardList}
            title="Sin conteos"
            hint="Cree el primer conteo para cuadrar el stock con lo que hay en bodega."
          />
        }
        onRowClick={c => setExpandedId(expandedId === c.id ? '' : c.id)}
        rowExpanded={c => expandedId === c.id}
        // Sin padding: `CountDetail` trae su propia tabla de lineas, que es por
        // lo que la celda del detalle no hereda el de `.cl-table td`.
        expandedRow={c => <CountDetail countId={c.id} fmtQty={fmtQty} />}
        caption="Conteos de inventario"
      />

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      <CountModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} isPending={isPending} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Count Actions ──────────────────────────────────────────────────────────

function CountActions({ count, navigate, toast, can }: {
  count: StockCount; navigate: any; toast: any; can: (m: string, a: string) => boolean
}) {
  const { mutate: complete, isPending: completing } = useCompleteStockCount(count.id)
  const { mutate: cancel, isPending: cancelling } = useCancelStockCount(count.id)
  const { confirm, modalProps } = useConfirmModal()

  const handleComplete = () => {
    confirm({
      message: '¿Completar este conteo? Se generarán ajustes automáticos por las diferencias encontradas.',
      variant: 'success',
      onConfirm: () => complete(undefined, {
        onSuccess: (data: any) => toast.success(`Conteo completado. ${data.adjustments_count || 0} ajustes generados.`),
        onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al completar')),
      }),
    })
  }

  const handleCancel = () => {
    confirm({
      message: '¿Anular este conteo? No se aplicarán ajustes.',
      variant: 'danger',
      onConfirm: () => cancel(undefined, {
        onSuccess: () => toast.success('Conteo anulado'),
        onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al anular')),
      }),
    })
  }

  return (
    <div className="flex items-center justify-center gap-1">
      {/* Escanear — solo in_progress */}
      {count.status === 'in_progress' && (
        <button onClick={() => navigate(`/inventory/counts/${count.id}/scan`)}
          title="Escanear con cámara" className="p-1.5 rounded-lg hover:bg-success-surface text-success-solid">
          <Camera size={15} />
        </button>
      )}
      {/* Completar — draft o in_progress */}
      {['draft', 'in_progress'].includes(count.status) && can('inventory.counts', 'approve') && (
        <button onClick={handleComplete} disabled={completing}
          title="Completar conteo" className="p-1.5 rounded-lg hover:bg-success-surface text-content-subtle hover:text-success-content-soft dark:text-emerald-300 disabled:opacity-40">
          <CheckSquare size={15} />
        </button>
      )}
      {/* Anular — draft o in_progress */}
      {['draft', 'in_progress'].includes(count.status) && can('inventory.counts', 'approve') && (
        <button onClick={handleCancel} disabled={cancelling}
          title="Anular conteo" className="p-1.5 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-solid disabled:opacity-40">
          <XCircle size={15} />
        </button>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}

// ─── Count Detail (lines table) ─────────────────────────────────────────────

function CountDetail({ countId, fmtQty }: { countId: string; fmtQty: (v: string | number) => string }) {
  const { data: count, isLoading } = useStockCount(countId)
  const { mutate: updateCount } = useUpdateStockCount(countId)
  const [addingProduct, setAddingProduct] = useState(false)
  const [products, setProducts] = useState<any[]>([])
  const [selectedProduct, setSelectedProduct] = useState('')

  const lines = count?.lines || []
  const isEditable = count && ['draft', 'in_progress'].includes(count.status)

  // Load products for add
  useEffect(() => {
    if (!addingProduct) return
    import('@/lib/apiClient').then(({ default: apiClient }) => {
      apiClient.get('/sales/products/', { params: { active: 'true', type: 'product', page_size: 9999 } })
        .then(res => setProducts(res.data?.products || []))
        .catch(() => {})
    })
  }, [addingProduct])

  const handleCountedChange = (index: number, value: string) => {
    const updated = lines.map((l: any, i: number) => {
      if (i !== index) return l
      const counted = parseFloat(value) || 0
      const expected = parseFloat(l.expected_qty || '0')
      return { ...l, counted_qty: String(counted), difference: String(counted - expected) }
    })
    updateCount({ lines: updated } as any)
  }

  const handleAddProduct = () => {
    if (!selectedProduct) return
    const prod = products.find((p: any) => p.id === selectedProduct)
    if (!prod) return
    // Check not already in lines
    if (lines.some((l: any) => l.product_id === prod.id)) return

    const updated = [...lines, {
      product_id: prod.id,
      product_sku: prod.sku,
      product_name: prod.name,
      expected_qty: '0',  // No estaba en sistema
      counted_qty: '0',
      difference: '0',
      // Sin costo: lo resuelve el backend con el costo que el producto ya tiene.
      // Mandar `unit_price` valorizaba el stock al PRECIO DE VENTA (OBS-M08-07).
      unit_cost: '',
    }]
    updateCount({ lines: updated } as any)
    setSelectedProduct('')
    setAddingProduct(false)
  }

  const handleRemoveLine = (index: number) => {
    const updated = lines.filter((_: any, i: number) => i !== index)
    updateCount({ lines: updated } as any)
  }

  /**
   * Las filas con su posición, la diferencia ya calculada y si el producto no
   * estaba en el stock esperado.
   *
   * ⚠️ Los tres salen del dato y no del `render`: la posición porque los
   * handlers la reciben, y los otros dos porque se usan en dos columnas.
   */
  const filasDeLinea = useMemo<FilaDeConteo[]>(
    () => lines.map((l: any, idx: number) => ({
      ...l,
      idx,
      diff: parseFloat(l.difference || '0'),
      esNuevo: parseFloat(l.expected_qty || '0') === 0 && l.counted_qty !== '0',
    })),
    [lines],
  )

  if (isLoading) return <div className="px-6 py-4 text-center text-sm text-content-subtle">Cargando líneas...</div>
  if (lines.length === 0 && !isEditable) return <div className="px-6 py-4 text-center text-sm text-content-subtle">Sin líneas registradas.</div>

  return (
    <div className="bg-surface-muted border-b border-divider">
      {/*
        Las filas llevan su POSICIÓN adentro (`filasDeLinea`): `handleCountedChange`
        y `handleRemoveLine` reciben el índice del array, y el `render` recibe el
        de la fila ya ordenada. Con el índice en el dato los dos coinciden.
      */}
      <DataTable<FilaDeConteo>
        columns={[
          { key: 'product_name', header: 'Producto',
            render: f => (
              <>
                {f.product_name}
                {f.esNuevo && (
                  <span className="ml-2 text-xs text-success-content-soft font-medium">Nuevo</span>
                )}
              </>
            ) },
          { key: 'product_sku', header: 'SKU',
            render: f => <span className="text-content-subtle font-mono">{f.product_sku}</span> },
          { key: 'expected_qty', header: 'Esperado', numeric: true,
            render: f => <span className="text-content-muted">{fmtQty(f.expected_qty || '0')}</span> },
          { key: 'counted_qty', header: 'Contado', numeric: true,
            render: f => (isEditable ? (
              <input type="number" value={f.counted_qty || ''} min="0" step="1"
                onChange={e => handleCountedChange(f.idx, e.target.value)}
                aria-label={`Cantidad contada de ${f.product_name}`}
                className="w-20 px-2 py-1 text-sm text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-primary" />
            ) : (
              <span className="font-medium">{fmtQty(f.counted_qty || '0')}</span>
            )) },
          { key: 'difference', header: 'Diferencia', numeric: true,
            render: f => (
              <span className={`font-bold ${
                f.diff > 0 ? 'text-success-content-soft'
                  : f.diff < 0 ? 'text-danger-content-soft'
                  : 'text-content-subtle'
              }`}>
                {f.diff > 0 ? '+' : ''}{fmtQty(f.diff)}
              </span>
            ) },
          ...(isEditable ? [{
            key: 'quitar',
            width: 32,
            header: '',
            render: (f: FilaDeConteo) => (
              <div className="text-center">
                <button onClick={() => handleRemoveLine(f.idx)} title="Quitar"
                  className="p-1 text-content-subtle hover:text-danger-solid">
                  <XCircle size={14} />
                </button>
              </div>
            ),
          }] : []),
        ]}
        rows={filasDeLinea}
        rowKey={f => f.idx}
        surface={false}
        rowClassName={f => (f.esNuevo ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : undefined)}
        caption="Líneas del conteo"
      />

      {/* Agregar producto no listado */}
      {isEditable && (
        <div className="px-6 py-3 border-t border-divider">
          {addingProduct ? (
            <div className="flex items-center gap-3">
              <select aria-label="Seleccionar producto" value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content">
                <option value="">Seleccionar producto...</option>
                {products
                  .filter((p: any) => !lines.some((l: any) => l.product_id === p.id))
                  .map((p: any) => (
                    <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                  ))}
              </select>
              <button onClick={handleAddProduct} disabled={!selectedProduct}
                className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40">
                Agregar
              </button>
              <button onClick={() => setAddingProduct(false)}
                className="px-3 py-1.5 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft">
                Cancelar
              </button>
            </div>
          ) : (
            <button onClick={() => setAddingProduct(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-hover">
              <Plus size={14} /> Agregar producto no listado
            </button>
          )}
        </div>
      )}

      {count?.notes && (
        <div className="px-6 py-3 border-t border-divider">
          <p className="text-xs text-content-subtle">Observaciones: <span className="text-content-muted">{count.notes}</span></p>
        </div>
      )}
    </div>
  )
}
